import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { classDerivedDeviceEnabledTypes } from '@iotv/datamodel';
import config from '../../../config';
import { TypeIcons } from '../../../Iconography';
import { ContainerLinkTreeItemFns } from '../categoriesAndRoutes/LSCContainerLinkTreeItems';
import { TreeContextCustomer, TreeCustomers } from '../TreeDefinitions/Customers';
import { DashboardTree } from '../TreeDefinitions/Dashboard';
import { Gateways } from '../TreeDefinitions/Gateways';
import { LNSTree } from '../TreeDefinitions/LNSTree';
import { ProgrammeTree } from '../TreeDefinitions/Programmes';
import { Region } from '../TreeDefinitions/Region';
import { EntityGroupTree, SpecificEntityGroupTree } from '../TreeLeaves/Admin/AdminEntitiesTrees';
import { AdminEntityNetworkTree } from '../TreeLeaves/Admin/AdminEntityNetworkTree';
import { AdminGatewaysTree } from '../TreeLeaves/Admin/AdminGatewaysTrees';
import { AdminPropertyTree } from '../TreeLeaves/Admin/AdminPropertyTree';
import { AdminRegionTrees } from '../TreeLeaves/Admin/AdminRegionTree';
import { CustomerTreeItemLinkProducer } from '../TreeLeaves/TreeItemLinkProducer';
import { TreeObjectsAssociations } from '../TreeLeaves/TreeObjectsAssociations';
import { SpecificVisibleGroupTree } from '../TreeLeaves/VisibleGroupsTree';
import { AdminVideoHubTree } from '../TreeLeaves/Admin/AdminVideoHub';
const { REACT_APP_DEBUG, NODE_ENV } = process.env;
const debug = REACT_APP_DEBUG && false;
const isProduction = NODE_ENV === 'production';
export const isTreeStructureTerminator = (x) => {
    const c = x;
    return !!c && typeof c.treeFC === 'function';
};
export const isTreeStructurePruned = (x) => {
    const c = x;
    return !!c && typeof c.prune === 'boolean' && c.prune === true;
};
export const isTreeStructurePath = (x) => {
    const c = x;
    return !isTreeStructureTerminator(c) && !isTreeStructurePruned(c) && !!c?.next;
};
const getEntityTreeDefs = (section) => {
    const entityTreeDefs = Object.fromEntries(classDerivedDeviceEnabledTypes.map((objectTypeId) => {
        return [
            objectTypeId,
            {
                contextMenuConfig: {
                    objectTypeId: 'Group',
                    supportedType: objectTypeId,
                },
                treeFC: SpecificEntityGroupTree(objectTypeId, `${section}_${objectTypeId}`),
            },
        ];
    }));
    return entityTreeDefs;
};
/** Gets trees for each entity that only show Visible groups (groups linked to a user's user group by "group_allows_group" edge type ID) */
const getVisibleGroupTreeDefs = (section) => {
    const visibleTreeDefs = Object.fromEntries(classDerivedDeviceEnabledTypes.map((supportedType) => {
        return [
            supportedType,
            {
                contextMenuConfig: { objectTypeId: 'Group', supportedType: supportedType },
                treeFC: SpecificVisibleGroupTree(supportedType, `${section}_visible_${supportedType}`),
            },
        ];
    }));
    return visibleTreeDefs;
};
/** Partner Trees */
const getPartnerEntityTrees = () => {
    const partnerEntityTrees = {};
    classDerivedDeviceEnabledTypes.forEach((det) => {
        partnerEntityTrees[det] = {
            treeFC: (props) => {
                //<PartnerEntityTree {...{ partner: props.partner, entityTypeId: det as ValidModelType, uniqueTreeId: `partner-${det}-tree`, }} />
                return props.partner ? (_jsx(EntityGroupTree, { contextObject: props.partner,
                    entityTypeId: det,
                    nodeEdgeMapper: props.applicationData,
                    transactionFunctions: props.transactionFunctions,
                    uniqueTreeId: `partner-${det}-Tree` })) : (_jsx(_Fragment, {}));
            },
        };
    });
    return partnerEntityTrees;
};
/**
 * Produces a default tree definition for projects not implementing domains
 *
 * **WARN**: will be **OVERWRITTEN** by a provided external menu structure (you can import and merge though)
 */
export const getNoDomainMappingTreeDef = () => {
    const treeDef = {
        User: { next: { ...getVisibleGroupTreeDefs('User') } },
        SetUp: {
            next: {
                ...getVisibleGroupTreeDefs('Setup'),
                Devices: { treeFC: SpecificVisibleGroupTree('Device', `SetUp_visible_devices`) },
                Controls: { next: ProgrammeTree },
                Places: {
                    next: {
                        Regions: {},
                        Properties: {},
                    },
                },
            },
        },
        Admin: {
            next: {
                ...getEntityTreeDefs('Admin'),
                Devices: { treeFC: SpecificEntityGroupTree('Device', `Admin_Device`) },
                Places: {
                    next: {
                        Regions: { treeFC: AdminRegionTrees },
                        Properties: { treeFC: AdminPropertyTree },
                    },
                },
            },
        },
        Partner: {},
    };
    debug && console.log('no domain mapping tree definition:', treeDef);
    return treeDef;
};
/** Constant menu items, will always be present and merged with external tree structure or domain tree mapping */
export const getTopLevelSectionDefintions = () => {
    return {
        Dashboard: { treeFC: DashboardTree },
        User: {
            ...Object.fromEntries(Object.keys(config.app.webUI.domainMap).map((domain) => [domain, { label: domain }])),
        },
        SetUp: {
            label: 'Set Up',
            next: {
                Customers: { treeFC: TreeContextCustomer },
                Users: { treeFC: CustomerTreeItemLinkProducer({ label: 'Users', tabName: 'User', endIcon: TypeIcons.User }) },
                Team: { treeFC: CustomerTreeItemLinkProducer({ label: 'Teams', tabName: 'Team', endIcon: TypeIcons.Team }) },
                Partners: { treeFC: ContainerLinkTreeItemFns.Partners },
                ...Object.fromEntries(Object.keys(config.app.webUI.domainMap).map((domain) => [domain, { label: domain }])),
                Devices: {},
                Places: {
                    next: {
                        Regions: { treeFC: Region },
                        Properties: { treeFC: AdminPropertyTree },
                    },
                },
                EntityNetworks: (!config.app.webUI.pruneEntityNetworks ? { treeFC: AdminEntityNetworkTree } : { prune: true }),
                Controls: { next: ProgrammeTree },
                Gateways: { treeFC: Gateways },
                Notifications: {
                    next: {
                        Alerts: { treeFC: ContainerLinkTreeItemFns.Alerts },
                        Rulesets: { treeFC: ContainerLinkTreeItemFns.Rulesets },
                        Incidents: { treeFC: ContainerLinkTreeItemFns.Incidents },
                        Subscriptions: { treeFC: ContainerLinkTreeItemFns.Subscriptions },
                        MobileDevices: { treeFC: ContainerLinkTreeItemFns.MobileDevices },
                        Notifications: { treeFC: ContainerLinkTreeItemFns.Notifications },
                    },
                },
            },
        },
        Admin: {
            next: {
                Customers: { treeFC: TreeCustomers },
                Users: { treeFC: CustomerTreeItemLinkProducer({ label: 'Users', tabName: 'User', endIcon: TypeIcons.User }) },
                Teams: { treeFC: CustomerTreeItemLinkProducer({ label: 'Teams', tabName: 'Team', endIcon: TypeIcons.Team }) },
                ...Object.fromEntries(Object.keys(config.app.webUI.domainMap).map((domain) => [domain, { label: domain }])),
                Devices: {},
                Places: {
                    next: {
                        Regions: { treeFC: AdminRegionTrees },
                        Properties: { treeFC: AdminPropertyTree },
                    },
                },
                EntityNetworks: (!config.app.webUI.pruneEntityNetworks ? { treeFC: AdminEntityNetworkTree } : { prune: true }),
                Controls: { next: ProgrammeTree },
                Gateways: { treeFC: AdminGatewaysTree },
                VideoHubs: { treeFC: AdminVideoHubTree },
                Notifications: {
                    next: {
                        Alerts: { treeFC: ContainerLinkTreeItemFns.Alerts },
                        Rulesets: { treeFC: ContainerLinkTreeItemFns.Rulesets },
                        Incidents: { treeFC: ContainerLinkTreeItemFns.Incidents },
                        Subscriptions: { treeFC: ContainerLinkTreeItemFns.Subscriptions },
                        MobileDevices: { treeFC: ContainerLinkTreeItemFns.MobileDevices },
                        Notifications: { treeFC: ContainerLinkTreeItemFns.Notifications },
                    },
                },
                Import_Export: { treeFC: ContainerLinkTreeItemFns.ImportExport },
            },
        },
        Instrumentation: {
            next: {
                Comparisons: { treeFC: ContainerLinkTreeItemFns.Comparison },
                // CRA — 2025 09: disabled as it breaks routing
                // Diagnostics: {
                //   // RSE: does not work, not included
                //   treeFC: ContainerLinkTreeItemFns.Diagnostic,
                // },
            },
        },
        Operations: {
            next: {
                LNS: {
                    next: {
                        Integration: { treeFC: ContainerLinkTreeItemFns.LNSIntegrations },
                        Instances: { treeFC: LNSTree },
                        NetworkList: { treeFC: ContainerLinkTreeItemFns.NetworkList },
                    },
                },
                Gateways: {
                    next: {
                        AWS: { treeFC: ContainerLinkTreeItemFns.AwsGateways },
                        List: { treeFC: ContainerLinkTreeItemFns.GatewayList },
                    },
                },
                Messages: { treeFC: ContainerLinkTreeItemFns.NetworkMonitoring },
                Network: { treeFC: ContainerLinkTreeItemFns.DeviceSummary },
                PayloadParser: { treeFC: ContainerLinkTreeItemFns.PayloadParser },
                Coverage: { treeFC: ContainerLinkTreeItemFns.NetworkCoverage },
                Systems: {
                    next: {
                        SystemStatus: { treeFC: ContainerLinkTreeItemFns.SystemStatus },
                        Logs: { treeFC: ContainerLinkTreeItemFns.SystemLogs },
                        Metrics: { treeFC: ContainerLinkTreeItemFns.AwsMetrics },
                    },
                },
            },
        },
        // SystemsConfiguration: {},
        ObjectAssociations: {
            label: `Objects & Associations`,
            treeFC: TreeObjectsAssociations,
        },
        Partner: { next: { ...getPartnerEntityTrees() } },
        Developer: {
            next: {
                Broadcast: { treeFC: ContainerLinkTreeItemFns.Broadcast },
                CalculatedStates: { treeFC: ContainerLinkTreeItemFns.CalculatedStates },
                // Maps: { treeFC: ContainerLinkTreeItemFns.MapView },
                PayloadDefinitions: { treeFC: ContainerLinkTreeItemFns.PayloadDefinitions },
                ProjectConfig: { treeFC: ContainerLinkTreeItemFns.ProjectConfig },
                Wideawake: { treeFC: ContainerLinkTreeItemFns.Wideawake },
                Widget_Playground: (!isProduction ? { treeFC: ContainerLinkTreeItemFns.WidgetPlayground } : { prune: true }),
            },
        },
        PrivacyPolicy: { treeFC: ContainerLinkTreeItemFns.PrivacyPolicy },
    };
};
